/**
 * Dependency-free QR encoder (byte mode, versions 1–6, all EC levels) with
 * best-mask selection — enough for verify URLs in hosted CVs / PDF export
 * (REQ-2.10). A faithful implementation of the ISO/IEC 18004 algorithm; the
 * Galois-field, EC-block, and alignment-pattern constants are the spec's.
 * Versions ≥7 (which need a version-information block) are intentionally out of
 * scope — v6 holds ~105 bytes at EC level M, far more than any verify URL.
 *
 * Kept in `@epicredible/shared` so both the public SSR Worker (hosted CV) and
 * the PDF pipeline render identical codes. Output is a crisp SVG (ideal for
 * print/PDF) or a boolean module matrix (for tests).
 */

export type EcLevel = "L" | "M" | "Q" | "H";

// ── Galois field GF(256), primitive polynomial 0x11d ───────────────────────
const EXP = new Uint8Array(256);
const LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 256; i++) EXP[i] = EXP[i - 255]!;
})();

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[(LOG[a]! + LOG[b]!) % 255]!;
}

/** Reed–Solomon EC codewords for one data block. */
function ecCodewords(data: number[], ecLen: number): number[] {
  // Generator polynomial of degree ecLen.
  let gen = [1];
  for (let i = 0; i < ecLen; i++) {
    const next = new Array<number>(gen.length + 1).fill(0);
    for (let j = 0; j < gen.length; j++) {
      next[j] = (next[j] ?? 0) ^ gfMul(gen[j]!, EXP[i]!);
      next[j + 1] = (next[j + 1] ?? 0) ^ gen[j]!;
    }
    gen = next;
  }
  const res = data.concat(new Array<number>(ecLen).fill(0));
  for (let i = 0; i < data.length; i++) {
    const coef = res[i]!;
    if (coef !== 0) {
      for (let j = 0; j < gen.length; j++) res[i + j] = (res[i + j] ?? 0) ^ gfMul(gen[j]!, coef);
    }
  }
  return res.slice(data.length);
}

// ── EC characteristics, versions 1–10 (ISO/IEC 18004 Table 9) ──────────────
// [ecPerBlock, [ [numBlocks, dataPerBlock], ... ]] keyed by version then level.
type BlockSpec = [number, [number, number][]];
const EC_TABLE: Record<EcLevel, BlockSpec[]> = {
  L: [
    [7, [[1, 19]]], [10, [[1, 34]]], [15, [[1, 55]]], [20, [[1, 80]]], [26, [[1, 108]]],
    [18, [[2, 68]]], [20, [[2, 78]]], [24, [[2, 97]]], [30, [[2, 116]]], [18, [[2, 68], [2, 69]]],
  ],
  M: [
    [10, [[1, 16]]], [16, [[1, 28]]], [26, [[1, 44]]], [18, [[2, 32]]], [24, [[2, 43]]],
    [16, [[4, 27]]], [18, [[4, 31]]], [22, [[2, 38], [2, 39]]], [22, [[3, 36], [2, 37]]], [26, [[4, 43], [1, 44]]],
  ],
  Q: [
    [13, [[1, 13]]], [22, [[1, 22]]], [18, [[2, 17]]], [26, [[2, 24]]], [18, [[2, 15], [2, 16]]],
    [24, [[4, 19]]], [18, [[2, 14], [4, 15]]], [22, [[4, 18], [2, 19]]], [20, [[4, 16], [4, 17]]], [24, [[6, 19], [2, 20]]],
  ],
  H: [
    [17, [[1, 9]]], [28, [[1, 16]]], [22, [[2, 13]]], [16, [[4, 9]]], [22, [[2, 11], [2, 12]]],
    [28, [[4, 15]]], [26, [[4, 13], [1, 14]]], [26, [[4, 14], [2, 15]]], [24, [[4, 12], [4, 13]]], [28, [[6, 15], [2, 16]]],
  ],
};

/** Alignment-pattern centre coordinates per version (empty for v1). */
const ALIGN_POS: number[][] = [
  [], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50],
];

/** Remainder bits appended after interleaved codewords, per version. */
const REMAINDER_BITS = [0, 7, 7, 7, 7, 7, 0, 0, 0, 0];

function dataCapacityCodewords(version: number, ec: EcLevel): number {
  const [, groups] = EC_TABLE[ec][version - 1]!;
  return groups.reduce((sum, [n, d]) => sum + n * d, 0);
}

// ── Bit buffer ─────────────────────────────────────────────────────────────
class BitBuffer {
  bits: number[] = [];
  put(value: number, length: number): void {
    for (let i = length - 1; i >= 0; i--) this.bits.push((value >>> i) & 1);
  }
}

/** Encode the payload into interleaved data+EC codewords for the chosen version. */
function encodeCodewords(bytes: number[], version: number, ec: EcLevel): number[] {
  const [ecPerBlock, groups] = EC_TABLE[ec][version - 1]!;
  const totalData = dataCapacityCodewords(version, ec);

  const bb = new BitBuffer();
  bb.put(0b0100, 4); // byte mode
  bb.put(bytes.length, version <= 9 ? 8 : 16);
  for (const b of bytes) bb.put(b, 8);
  // Terminator (up to 4 bits) then pad to a byte boundary.
  const capacityBits = totalData * 8;
  for (let i = 0; i < 4 && bb.bits.length < capacityBits; i++) bb.bits.push(0);
  while (bb.bits.length % 8 !== 0) bb.bits.push(0);

  const dataCodewords: number[] = [];
  for (let i = 0; i < bb.bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = (byte << 1) | bb.bits[i + j]!;
    dataCodewords.push(byte);
  }
  // Pad bytes 0xEC / 0x11 alternating.
  const pads = [0xec, 0x11];
  for (let i = 0; dataCodewords.length < totalData; i++) dataCodewords.push(pads[i % 2]!);

  // Split into blocks, compute EC per block.
  const dataBlocks: number[][] = [];
  const ecBlocks: number[][] = [];
  let offset = 0;
  for (const [numBlocks, dataPerBlock] of groups) {
    for (let b = 0; b < numBlocks; b++) {
      const block = dataCodewords.slice(offset, offset + dataPerBlock);
      offset += dataPerBlock;
      dataBlocks.push(block);
      ecBlocks.push(ecCodewords(block, ecPerBlock));
    }
  }

  // Interleave data codewords, then EC codewords.
  const result: number[] = [];
  const maxData = Math.max(...dataBlocks.map((b) => b.length));
  for (let i = 0; i < maxData; i++) {
    for (const block of dataBlocks) if (i < block.length) result.push(block[i]!);
  }
  for (let i = 0; i < ecPerBlock; i++) {
    for (const block of ecBlocks) result.push(block[i]!);
  }
  return result;
}

// ── Matrix construction ─────────────────────────────────────────────────────
type Grid = { size: number; modules: (boolean | null)[][] };

function newGrid(version: number): Grid {
  const size = version * 4 + 17;
  const modules: (boolean | null)[][] = Array.from({ length: size }, () =>
    new Array<boolean | null>(size).fill(null),
  );
  return { size, modules };
}

function placeFinder(g: Grid, row: number, col: number): void {
  for (let r = -1; r <= 7; r++) {
    for (let c = -1; c <= 7; c++) {
      const rr = row + r;
      const cc = col + c;
      if (rr < 0 || rr >= g.size || cc < 0 || cc >= g.size) continue;
      const on =
        (r >= 0 && r <= 6 && (c === 0 || c === 6)) ||
        (c >= 0 && c <= 6 && (r === 0 || r === 6)) ||
        (r >= 2 && r <= 4 && c >= 2 && c <= 4);
      g.modules[rr]![cc] = on;
    }
  }
}

function placeAlignment(g: Grid, version: number): void {
  const pos = ALIGN_POS[version - 1]!;
  for (const r of pos) {
    for (const c of pos) {
      // Skip the three finder corners.
      if ((r === 6 && c === 6) || (r === 6 && c === pos[pos.length - 1]) || (c === 6 && r === pos[pos.length - 1])) {
        continue;
      }
      if (g.modules[r]![c] !== null) continue;
      for (let dr = -2; dr <= 2; dr++) {
        for (let dc = -2; dc <= 2; dc++) {
          const on = Math.max(Math.abs(dr), Math.abs(dc)) !== 1;
          g.modules[r + dr]![c + dc] = on;
        }
      }
    }
  }
}

function placeFunctionPatterns(g: Grid, version: number): void {
  placeFinder(g, 0, 0);
  placeFinder(g, 0, g.size - 7);
  placeFinder(g, g.size - 7, 0);
  // Timing patterns.
  for (let i = 8; i < g.size - 8; i++) {
    const on = i % 2 === 0;
    if (g.modules[6]![i] === null) g.modules[6]![i] = on;
    if (g.modules[i]![6] === null) g.modules[i]![6] = on;
  }
  placeAlignment(g, version);
  // Dark module.
  g.modules[g.size - 8]![8] = true;
}

/** Reserve format/version areas so data placement skips them. */
function reservedForFormat(g: Grid, r: number, c: number): boolean {
  const n = g.size;
  if (r === 6 || c === 6) return true; // timing
  if (r < 9 && c < 9) return true; // top-left finder + format
  if (r < 9 && c >= n - 8) return true; // top-right finder + format
  if (r >= n - 8 && c < 9) return true; // bottom-left finder + format
  return false;
}

const FORMAT_EC_BITS: Record<EcLevel, number> = { L: 0b01, M: 0b00, Q: 0b11, H: 0b10 };

function formatBits(ec: EcLevel, mask: number): number {
  const data = (FORMAT_EC_BITS[ec] << 3) | mask;
  let rem = data << 10;
  const g = 0b10100110111;
  for (let i = 14; i >= 10; i--) if ((rem >>> i) & 1) rem ^= g << (i - 10);
  return ((data << 10) | rem) ^ 0b101010000010010;
}

function placeFormat(g: Grid, ec: EcLevel, mask: number): void {
  const bits = formatBits(ec, mask);
  const n = g.size;
  for (let i = 0; i < 15; i++) {
    const on = ((bits >>> i) & 1) === 1;
    // Around top-left.
    if (i < 6) g.modules[i]![8] = on;
    else if (i < 8) g.modules[i + 1]![8] = on;
    else if (i === 8) g.modules[8]![7] = on;
    else g.modules[8]![14 - i] = on;
    // Around top-right / bottom-left.
    if (i < 8) g.modules[8]![n - 1 - i] = on;
    else g.modules[n - 15 + i]![8] = on;
  }
}

function maskFn(mask: number, r: number, c: number): boolean {
  switch (mask) {
    case 0: return (r + c) % 2 === 0;
    case 1: return r % 2 === 0;
    case 2: return c % 3 === 0;
    case 3: return (r + c) % 3 === 0;
    case 4: return (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0;
    case 5: return ((r * c) % 2) + ((r * c) % 3) === 0;
    case 6: return (((r * c) % 2) + ((r * c) % 3)) % 2 === 0;
    default: return (((r + c) % 2) + ((r * c) % 3)) % 2 === 0;
  }
}

function placeData(g: Grid, codewords: number[], version: number, mask: number): void {
  const n = g.size;
  const bits: number[] = [];
  for (const cw of codewords) for (let i = 7; i >= 0; i--) bits.push((cw >>> i) & 1);
  for (let i = 0; i < REMAINDER_BITS[version - 1]!; i++) bits.push(0);

  let bitIdx = 0;
  let upward = true;
  for (let col = n - 1; col > 0; col -= 2) {
    if (col === 6) col--; // skip timing column
    for (let i = 0; i < n; i++) {
      const row = upward ? n - 1 - i : i;
      for (let j = 0; j < 2; j++) {
        const c = col - j;
        if (g.modules[row]![c] !== null || reservedForFormat(g, row, c)) continue;
        let bit = bitIdx < bits.length ? bits[bitIdx]! === 1 : false;
        bitIdx++;
        if (maskFn(mask, row, c)) bit = !bit;
        g.modules[row]![c] = bit;
      }
    }
    upward = !upward;
  }
}

function penalty(g: Grid): number {
  const n = g.size;
  const m = g.modules as boolean[][];
  let score = 0;
  // Rule 1: runs of ≥5 same-colour modules (rows + cols).
  for (let r = 0; r < n; r++) {
    for (const axis of [0, 1]) {
      let run = 1;
      let prev = axis === 0 ? m[r]![0]! : m[0]![r]!;
      for (let i = 1; i < n; i++) {
        const cur = axis === 0 ? m[r]![i]! : m[i]![r]!;
        if (cur === prev) {
          run++;
          if (run === 5) score += 3;
          else if (run > 5) score += 1;
        } else {
          run = 1;
          prev = cur;
        }
      }
    }
  }
  // Rule 2: 2×2 blocks of same colour.
  for (let r = 0; r < n - 1; r++) {
    for (let c = 0; c < n - 1; c++) {
      const v = m[r]![c]!;
      if (v === m[r]![c + 1] && v === m[r + 1]![c] && v === m[r + 1]![c + 1]) score += 3;
    }
  }
  // Rule 3: finder-like 1:1:3:1:1 patterns.
  const pat1 = [true, false, true, true, true, false, true, false, false, false, false];
  const pat2 = [false, false, false, false, true, false, true, true, true, false, true];
  for (let r = 0; r < n; r++) {
    for (let c = 0; c <= n - 11; c++) {
      const rowMatch = (p: boolean[]) => p.every((v, k) => m[r]![c + k] === v);
      const colMatch = (p: boolean[]) => p.every((v, k) => m[c + k]![r] === v);
      if (rowMatch(pat1) || rowMatch(pat2)) score += 40;
      if (colMatch(pat1) || colMatch(pat2)) score += 40;
    }
  }
  // Rule 4: proportion of dark modules.
  let dark = 0;
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (m[r]![c]) dark++;
  const pct = (dark * 100) / (n * n);
  score += Math.floor(Math.abs(pct - 50) / 5) * 10;
  return score;
}

function chooseVersion(byteLen: number, ec: EcLevel): number {
  // Cap at 6: versions ≥7 need a version-information block we don't emit, and
  // verify URLs never approach v6's capacity (~105 bytes at EC level M).
  for (let v = 1; v <= 6; v++) {
    const countBits = v <= 9 ? 8 : 16;
    const capacityBits = dataCapacityCodewords(v, ec) * 8 - 4 - countBits;
    if (byteLen * 8 <= capacityBits) return v;
  }
  throw new Error("QR payload too large (max ~105 bytes at EC level M)");
}

export interface QrMatrix {
  size: number;
  /** true = dark module. */
  modules: boolean[][];
}

/** Build the QR module matrix for `text` (UTF-8, byte mode). */
export function encodeQr(text: string, ec: EcLevel = "M"): QrMatrix {
  const bytes = [...new TextEncoder().encode(text)];
  const version = chooseVersion(bytes.length, ec);
  const codewords = encodeCodewords(bytes, version, ec);

  let best: Grid | null = null;
  let bestScore = Infinity;
  let bestMask = 0;
  for (let mask = 0; mask < 8; mask++) {
    const g = newGrid(version);
    placeFunctionPatterns(g, version);
    placeData(g, codewords, version, mask);
    placeFormat(g, ec, mask);
    const score = penalty(g);
    if (score < bestScore) {
      bestScore = score;
      best = g;
      bestMask = mask;
    }
  }
  // Re-place format with the winning mask (already set, but keep explicit).
  placeFormat(best!, ec, bestMask);
  return { size: best!.size, modules: best!.modules.map((row) => row.map((v) => v === true)) };
}

/**
 * Render `text` as a QR SVG string. `size` is the pixel side length; a 4-module
 * quiet zone is included per spec.
 */
export function qrToSvg(text: string, opts: { size?: number; ec?: EcLevel } = {}): string {
  const { size = 160, ec = "M" } = opts;
  const { size: n, modules } = encodeQr(text, ec);
  const quiet = 4;
  const dim = n + quiet * 2;
  const cell = size / dim;
  let rects = "";
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (modules[r]![c]) {
        const x = ((c + quiet) * cell).toFixed(2);
        const y = ((r + quiet) * cell).toFixed(2);
        rects += `<rect x="${x}" y="${y}" width="${cell.toFixed(2)}" height="${cell.toFixed(2)}"/>`;
      }
    }
  }
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" ` +
    `viewBox="0 0 ${size} ${size}" role="img" aria-label="QR code">` +
    `<rect width="${size}" height="${size}" fill="#fff"/>` +
    `<g fill="#000">${rects}</g></svg>`
  );
}
